import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link, Users, Target, Image } from 'lucide-react';
import { FacebookContent } from '../types/socialMedia.types';
import CharacterCounter from '../components/CharacterCounter';
import ScheduleSettings from '../components/ScheduleSettings';
import MediaUploader from '../components/MediaUploader';
import { PLATFORMS } from '../config/platformConfig';

interface FacebookEditorProps {
  content: FacebookContent;
  isEditing: boolean;
  onChange: (field: keyof FacebookContent, value: any) => void;
}

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    ['link'],
    ['clean']
  ]
};

export default function FacebookEditor({ content, isEditing, onChange }: FacebookEditorProps) {
  const platform = PLATFORMS.facebook;
  const characterCount = content.text ? content.text.replace(/<[^>]*>/g, '').length : 0;

  return (
    <div className="space-y-4">
      {/* Post Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Post Type</label>
        <select
          value={content.postType}
          onChange={(e) => onChange('postType', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        >
          {platform.postTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Post Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Post Text</label>
        {isEditing ? (
          <ReactQuill
            value={content.text || ''}
            onChange={(value) => onChange('text', value)}
            modules={quillModules}
            className="bg-white rounded"
            placeholder="What's on your mind?"
          />
        ) : (
          <div
            className="px-3 py-2 bg-gray-50 rounded min-h-[100px]"
            dangerouslySetInnerHTML={{
              __html: content.text || '<span class="text-gray-400">No content</span>'
            }}
          />
        )}
        <div className="mt-2">
          <CharacterCounter
            current={characterCount}
            limit={platform.limits.text!}
            label="Post text"
          />
        </div>
      </div>

      {/* Media Upload */}
      {(content.postType === 'Image' || content.postType === 'Video' || content.postType === 'Link') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Image className="inline w-4 h-4 mr-1" />
            Media Files
          </label>
          <MediaUploader
            media={content.media || []}
            onChange={(media) => onChange('media', media)}
            disabled={!isEditing}
            maxFiles={content.postType === 'Video' ? 1 : 10}
            acceptedTypes={content.postType === 'Video' ? 'video/*' : 'image/*,video/*'}
            platformName="Facebook"
          />
        </div>
      )}

      {/* Link */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Link className="inline w-4 h-4 mr-1" />
          Link URL
        </label>
        <input
          type="url"
          value={content.link}
          onChange={(e) => onChange('link', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          placeholder="https://example.com"
        />
      </div>

      {/* Call to Action */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Call to Action</label>
        <select
          value={content.callToAction}
          onChange={(e) => onChange('callToAction', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        >
          <option value="">No Button</option>
          <option value="learn_more">Learn More</option>
          <option value="shop_now">Shop Now</option>
          <option value="sign_up">Sign Up</option>
          <option value="download">Download</option>
          <option value="get_offer">Get Offer</option>
          <option value="contact_us">Contact Us</option>
          <option value="book_now">Book Now</option>
          <option value="watch_more">Watch More</option>
        </select>
      </div>

      {/* Audience Targeting */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Target className="inline w-4 h-4 mr-1" />
          Audience Targeting
        </label>
        <input
          type="text"
          value={content.targeting}
          onChange={(e) => onChange('targeting', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          placeholder="e.g., Age 25-45, Interests: Home Improvement"
        />
        <p className="text-xs text-gray-500 mt-1">
          Define your target audience demographics and interests
        </p>
      </div>

      {/* Schedule */}
      <ScheduleSettings
        scheduledDate={content.scheduledDate}
        scheduledTime={content.scheduledTime}
        onDateChange={(date) => onChange('scheduledDate', date)}
        onTimeChange={(time) => onChange('scheduledTime', time)}
        disabled={!isEditing}
        platformName="Facebook"
      />
    </div>
  );
}